import { breakpoints } from "@brightagency/headstart-common";
import { transparentize } from "polished";
import styled, { css } from "styled-components";
import { buttonResetStyles } from "../../../utils";
import { colors } from "../../../utils/colors";
import { RawSeparator } from "../../atoms/Separator";
import ChevronDownSvg from "./assets/chevron-down.svg";

export const FieldWrapper = styled.div<{ isHidden?: boolean }>`
    margin: 20px 0;

    input,
    textarea,
    select {
        display: block;
        width: 100%;
    }

    input,
    textarea,
    select {
        border: 1px solid #eaeaea;
        background-color: #fafbfc;
        padding: 12px;
        line-height: 1.4;
        color: ${colors.brandDark};
        border-radius: 12px;

        &:focus {
            box-shadow: 0 0 0 2px ${colors.brandMid};
            background-color: white;
        }
    }

    input {
        height: 56px;
    }

    textarea {
        height: 168px;
    }

    fieldset {
        border: 0;
        padding: 0;
    }

    input[type="checkbox"],
    input[type="radio"] {
        height: auto;
    }

    select {
        appearance: none;
        background-image: url(${ChevronDownSvg});
        background-size: 16px 16px;
        background-repeat: no-repeat;
        background-position: center right 16px;
    }

    ${({ isHidden }) =>
        isHidden &&
        css`
            display: none;
        `}
`;

export const Label = styled.label`
    display: block;
    font-size: 16px;
    margin-bottom: 8px;
    font-weight: 500;
    color: ${colors.brandDark};
`;

export const Description = styled.div`
    font-size: 16px;
    margin-bottom: 14px;
    color: #676767;
`;

export const Error = styled.div`
    font-size: 14px;
    margin-top: 8px;
    color: red;
`;

export const FieldSet = styled.fieldset`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const CheckboxRadioStyles = css`
    display: flex;
    align-items: center;
    gap: 8px;

    input {
        display: none;
        width: 20px;
    }
`;

export const RadioControl = styled.div`
    position: relative;
    width: 20px;
    height: 20px;
    border: 2px solid ${colors.brandDark};
    border-radius: 50%;
    overflow: hidden;

    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: ${colors.brandDark};
        transform: scale(0);
        border-radius: 50%;
        transition: 100ms ease all;
    }

    input:checked + & {
        border-color: ${colors.brandMid};
        &::before {
            transform: scale(0.7);
            background-color: ${colors.brandMid};
        }
    }
`;

export const CheckboxControl = styled.div`
    position: relative;
    width: 20px;
    height: 20px;
    border: 2px solid ${colors.brandDark};
    overflow: hidden;
    border-radius: 2px;

    &::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: ${colors.brandDark};
        background-position: center;
        background-repeat: no-repeat;
        transform: scale(0);
        transition: 100ms ease all;
        background-image: url("data:image/svg+xml,%3c%3fxml version='1.0' encoding='UTF-8'%3f%3e %3csvg width='24px' height='24px' viewBox='0 0 24 24' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3e%3ctitle%3ev%3c/title%3e%3cg id='v' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3e%3cpath d='M10.2404602%2c16.2404602 C9.85042273%2c16.6281838 9.22049773%2c16.6281838 8.83046023%2c16.2404602 L5.24046023%2c12.6504602 C4.90068343%2c12.2536999 4.92352793%2c11.662266 5.29289703%2c11.292897 C5.66226603%2c10.9235279 6.25369993%2c10.9006834 6.65046023%2c11.2404602 L9.53046023%2c14.1204602 L16.4104602%2c7.24046023 C16.8072205%2c6.90068343 17.3986544%2c6.92352793 17.7680234%2c7.29289703 C18.1373925%2c7.66226603 18.160237%2c8.25369993 17.8204602%2c8.65046023 L10.2404602%2c16.2404602 Z' id='Path_4698-path' fill='white' fill-rule='nonzero'%3e%3c/path%3e%3c/g%3e%3c/svg%3e");
    }

    input:checked + & {
        border-color: ${colors.brandMid};
        &::before {
            background-color: ${colors.brandMid};
            transform: scale(1);
        }
    }
`;

export const CheckboxWrapper = styled.label`
    ${CheckboxRadioStyles}
`;

export const RadioWrapper = styled.label`
    ${CheckboxRadioStyles}
`;

export const ServerError = styled.div`
    border: 1px solid red;
    padding: 24px;
    background-color: ${transparentize(0.8, "red")};
    border-radius: 5px;
    margin: 48px 0;

    > *:last-child {
        margin-bottom: 0;
    }
`;

export const ListRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;

    &:not(:last-child) {
        margin-bottom: 12px;
    }
`;

export const ListColumn = styled.div``;

export const ListButtons = styled.div`
    display: flex;
    gap: 8px;
    margin-top: 10px;
`;

export const ListButton = styled.button`
    position: relative;
    ${buttonResetStyles};
    background-color: ${colors.brandMid};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    height: 54px;
`;

export const SubmitButtonWrapper = styled.div<{ alignment: "center" | "left" }>`
    margin-top: 24px;
    width: 100%;

    ${({ alignment }) => {
        switch (alignment) {
            case "center":
                return css`
                    margin-left: auto;
                    margin-right: auto;
                `;
            default:
                return null;
        }
    }}

    ${breakpoints.medium} {
        max-width: 320px;
    }
`;

export const FormSection = styled.div`
    margin-top: 40px;

    ${RawSeparator} {
        margin-bottom: 16px;
    }
`;

export const LoaderStyleOverride = styled.div`
    margin-top: 24px;
    text-align: center;
    width:100%;
    display:flex;
    justify-content:center;

    svg {
        stroke: ${colors.brandDark};
    }
`;

